import { useCallback, useEffect, useState } from 'react';

import { authenticatedJsonFetch } from '@perpay-web/authentication/authenticatedJsonFetch';
import { useAnimatedModal } from '@perpay-web/components/base/Modal/useAnimatedModal';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import { NOTIFICATIONS_ENDPOINT } from '@perpay-web/fintech/constants/urls';
import {
    NotificationType,
    type Notification,
} from '@perpay-web/fintech/reducers/entities/notifications';
import { isHttpError } from '@perpay-web/fintech/utils/typeUtils';
import {
    fetchWithdrawalRequest,
    WITHDRAWAL_REQUEST_STATUS,
    type WithdrawalRequest,
} from '@perpay-web/fintech/utils/withdrawalRequestUtils';
import { createContextProvider } from '@perpay-web/utils/componentUtils';
import { reportError } from '@perpay-web/utils/errorHandlers';

import { useAcknowledgeNotification } from './useAcknowledgeNotification';

const findOverpaymentNotification = (notifications: Notification[]) =>
    notifications.find(
        (notification) =>
            notification.type === NotificationType.OVERPAYMENT &&
            !notification.acknowledged,
    );

type OverpaymentDecisionModalContextValue = {
    isModalOpen: boolean;
    setIsModalOpen(isOpen: boolean): void;
    animatedModalClassName: string;
    withdrawalRequest?: WithdrawalRequest | null;
    refetchNotifications(): Promise<void>;
};

const useOverpaymentDecisionModal =
    (): OverpaymentDecisionModalContextValue => {
        const acknowledgeNotificationAction = useAcknowledgeNotification();
        const { isModalOpen, setIsModalOpen } = useModal();

        const {
            isAnimatedModalOpen,
            setIsAnimatedModalOpen,
            animatedModalClassName,
        } = useAnimatedModal({
            isModalOpen,
            setIsModalOpen,
        });

        const [withdrawalRequest, setWithdrawalRequest] =
            useState<WithdrawalRequest | null>();
        const fetchNotifications = useCallback(async () => {
            try {
                const notifications = await authenticatedJsonFetch<
                    Notification[]
                >(NOTIFICATIONS_ENDPOINT);
                const overpaymentNotification =
                    findOverpaymentNotification(notifications);
                if (!overpaymentNotification) {
                    setWithdrawalRequest(null);
                    return;
                }

                const { withdrawalRequestUuid } =
                    overpaymentNotification.payload;
                if (!withdrawalRequestUuid) {
                    setWithdrawalRequest(null);
                    return;
                }
                const wr = await fetchWithdrawalRequest(withdrawalRequestUuid);
                if (wr.status === WITHDRAWAL_REQUEST_STATUS.SUBMITTED) {
                    setWithdrawalRequest(wr);
                    setIsAnimatedModalOpen(true);
                } else {
                    acknowledgeNotificationAction(overpaymentNotification.uuid);
                    setWithdrawalRequest(null);
                }
            } catch (error) {
                // Only suppress 401 errors, report everything else
                if (!(isHttpError(error) && error.status === 401)) {
                    reportError(error);
                }
            }
        }, [acknowledgeNotificationAction, setIsAnimatedModalOpen]);

        useEffect(() => {
            fetchNotifications();
        }, [fetchNotifications]);

        return {
            isModalOpen: isAnimatedModalOpen,
            setIsModalOpen: setIsAnimatedModalOpen,
            animatedModalClassName,
            withdrawalRequest,
            refetchNotifications: fetchNotifications,
        };
    };

export const [
    OverpaymentDecisionModalProvider,
    useOverpaymentDecisionModalContext,
] = createContextProvider<OverpaymentDecisionModalContextValue>(
    (Context) =>
        ({ children }) => (
            <Context.Provider value={useOverpaymentDecisionModal()}>
                {children}
            </Context.Provider>
        ),
);
